<template>
  <div class="flex items-stretch justify-center min-h-screen bg-slate-100 text-gray-800">

    <div
      class="hidden lg:flex w-1/2 flex-col items-center justify-center bg-gray-900 text-white p-12 relative overflow-hidden">

      <div class="absolute inset-0 bg-cover bg-center" :style="{ backgroundImage: `url(${backgroundImageUrl})` }">
      </div>
      <div class="absolute inset-0 bg-black/70"></div>

      <div class="z-10 text-center">
        <img src="@/assets/images/logo.png" alt="Graha Sumber Prima Elektronik Logo" class="mx-auto h-24 w-auto">
        <h1 class="text-4xl font-bold mt-6">Graha Sumber Prima Elektronik</h1>
        <p class="text-slate-300 mt-4 text-lg">POWER IT</p>
      </div>

      <div class="absolute bottom-4 text-xs text-slate-500 z-10">
        &copy; {{ new Date().getFullYear() }} GSPE. All Rights Reserved.
      </div>
    </div>

    <div class="w-full lg:w-1/2 flex items-center justify-center p-8">
      <div class="w-full max-w-sm">
        <div class="text-left mb-10">
          <h2 class="text-3xl font-bold">Selamat Datang</h2>
          <p class="text-slate-500 mt-2">Silakan login untuk mengakses dashboard Anda.</p>
        </div>

        <form @submit.prevent="login">
          <template v-if="enableLocalLogin">
            <div class="mb-5">
              <label class="block text-slate-700 text-sm font-semibold mb-2" for="username">
                Username
              </label>
              <div class="relative">
                <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                  <UserCircleIcon class="w-5 h-5" />
                </span>
                <input
                  class="appearance-none border-b-2 w-full py-3 pl-10 pr-3 text-slate-700 bg-transparent leading-tight focus:outline-none focus:border-sky-500 transition-colors duration-300"
                  id="username" type="text" placeholder="Masukkan username" v-model="username" required />
              </div>
            </div>

            <div class="mb-8">
              <label class="block text-slate-700 text-sm font-semibold mb-2" for="password">
                Password
              </label>
              <div class="relative">
                <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                  <LockClosedIcon class="w-5 h-5" />
                </span>
                <input
                  class="appearance-none border-b-2 w-full py-3 pl-10 pr-10 text-slate-700 bg-transparent leading-tight focus:outline-none focus:border-sky-500 transition-colors duration-300"
                  id="password" :type="isPasswordVisible ? 'text' : 'password'" placeholder="Masukkan password"
                  v-model="password" required />
                <button @click="togglePasswordVisibility" type="button"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-sky-600 cursor-pointer">
                  <EyeIcon v-if="isPasswordVisible" class="w-5 h-5" />
                  <EyeSlashIcon v-else class="w-5 h-5" />
                </button>
              </div>
            </div>

            <div class="flex items-center justify-between mb-8">
              <label class="inline-flex items-center text-sm text-slate-600" for="remember">
                <input type="checkbox" id="remember"
                  class="mr-2 form-checkbox h-4 w-4 text-sky-600 rounded-sm border-slate-300 focus:ring-sky-500" />
                Ingat saya
              </label>
              <a href="#" class="inline-block align-baseline font-bold text-sm text-sky-600 hover:text-sky-800">
                Lupa Password?
              </a>
            </div>

            <div class="flex items-center justify-center">
              <button
                class="w-full bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
                type="submit">
                Login
              </button>
            </div>

            <div class="flex items-center my-6">
              <div class="flex-grow border-t border-slate-300"></div>
              <span class="mx-4 text-sm text-slate-400">atau</span>
              <div class="flex-grow border-t border-slate-300"></div>
            </div>
          </template>

          <div class="flex items-center justify-center">
            <button
              class="w-full bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              type="button" @click="loginWithSso">
              Masuk dengan Authentik
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { UserCircleIcon, LockClosedIcon, EyeSlashIcon, EyeIcon } from '@heroicons/vue/24/outline';
// Impor gambar latar belakang secara langsung di dalam script
import backgroundImageUrl from '@/assets/images/background.png';

export default {
  name: 'LoginPage',
  components: {
    UserCircleIcon, LockClosedIcon, EyeSlashIcon, EyeIcon,
  },
  data() {
    return {
      // Daftarkan variabel gambar agar bisa diakses di template
      backgroundImageUrl,
      // Sign-in is SSO-only. The username/password form is shown only when
      // VUE_APP_ENABLE_LOCAL_LOGIN is "true" (e.g. during an SSO outage); the
      // backend /login endpoint is unchanged.
      enableLocalLogin: process.env.VUE_APP_ENABLE_LOCAL_LOGIN === 'true',
      username: '',
      password: '',
      isPasswordVisible: false,
    };
  },
  mounted() {
    // Sesi dipaksa berakhir oleh interceptor 401 (token expired/invalid)
    if (this.$route.query.expired) {
      Swal.fire({
        icon: 'warning',
        title: 'Sesi Berakhir',
        text: 'Sesi login Anda sudah berakhir. Silakan login kembali.',
        confirmButtonColor: '#0284c7',
      });
      this.$router.replace({ path: '/login' });
      return;
    }
    // Tampilkan error SSO dari redirect backend (?sso_error=...)
    const ssoError = this.$route.query.sso_error;
    if (ssoError) {
      const messages = {
        missing_code: 'Login SSO gagal: kode otorisasi tidak ditemukan.',
        invalid_state: 'Login SSO gagal: state tidak valid atau kadaluarsa.',
        token_failed: 'Login SSO gagal: gagal menukar token Authentik.',
        userinfo_failed: 'Login SSO gagal: gagal mengambil data user dari Authentik.',
        no_email: 'Login SSO gagal: email tidak ditemukan di akun Authentik.',
        authserver_unreachable: 'Login SSO gagal: Authserver tidak dapat dihubungi.',
        unmapped_user: 'Email Authentik belum terdaftar di Authserver.',
        user_not_registered: 'User belum terdaftar di aplikasi SPK Subcon.',
      };
      Swal.fire({
        icon: 'error',
        title: 'Login SSO Gagal',
        text: messages[ssoError] || `Login SSO gagal: ${ssoError}`,
        confirmButtonColor: '#0284c7',
      });
      this.$router.replace({ path: '/login' });
    }
  },
  methods: {
    async login() {
      try {
        const response = await axios.post(`${window.location.origin}/login`, {
          username: this.username,
          password: this.password,
        });

        const token = response.data;
        localStorage.setItem('token', token);
        this.$router.push('/dashboard');
      } catch (err) {
        const errorMessage = err.response ? err.response.data : 'Login gagal. Periksa kembali username dan password Anda.';
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: errorMessage,
          confirmButtonColor: '#0284c7' // sky-600
        });
      }
    },
    loginWithSso() {
      window.location.href = `${window.location.origin}/auth/sso`;
    },
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible;
    },
  },
};
</script>

<style scoped>
/* Kustomisasi form checkbox agar lebih modern */
.form-checkbox:checked {
  background-color: #0ea5e9;
  /* sky-500 */
}
</style>